import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import KPIGrid from "@/components/dashboard/KPIGrid";
import DecisionsPanel from "@/components/dashboard/DecisionsPanel";
import LeaderboardPanel from "@/components/dashboard/LeaderboardPanel";
import RoundDetailPanel from "@/components/RoundDetailPanel";
import PerformanceHistory from "@/components/PerformanceHistory";
import MarketAlertBanner from "@/components/MarketAlertBanner";
import CopilotChat from "@/components/CopilotChat";
import TeamChat from "@/components/TeamChat";
import { Shield, CheckCircle } from "lucide-react";

export default function HospitalDashboard() {
  const { simId, hospitalId: routeHospitalId } = useParams();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { t } = useTranslation();

  // God Mode: admin/instructor accessing a specific hospital via /view/hospital/:id
  const isGodMode = !!routeHospitalId && (user.role === "superadmin" || user.role === "instructor");
  const isStudentView = !routeHospitalId && user.role === "student";

  const [simulation, setSimulation] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [history, setHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [decision, setDecision] = useState(null);
  const [wasSubmitted, setWasSubmitted] = useState(false);

  const loadData = useCallback(async () => {
    try {
      let myHosp;
      if (routeHospitalId) {
        const hospRes = await axios.get(`${API}/hospitals/${routeHospitalId}`);
        myHosp = hospRes.data;
        setHospital(myHosp);
        const simRes = await axios.get(`${API}/simulations/${myHosp.simulation_id}`);
        setSimulation(simRes.data);
        const [kpiRes, histRes, lbRes] = await Promise.all([
          axios.get(`${API}/hospitals/${myHosp.id}/kpis`),
          axios.get(`${API}/hospitals/${myHosp.id}/history`),
          axios.get(`${API}/simulations/${myHosp.simulation_id}/leaderboard`),
        ]);
        setKpis(kpiRes.data);
        setHistory(histRes.data);
        setLeaderboard(lbRes.data);
        if (simRes.data.current_round > 0) {
          try {
            const decRes = await axios.get(`${API}/hospitals/${myHosp.id}/decisions/${simRes.data.current_round}`);
            const dec = decRes.data;
            if (isGodMode && dec.submitted) {
              setWasSubmitted(true);
              dec.submitted = false;
              dec.submitted_at = null;
            } else {
              setWasSubmitted(false);
            }
            setDecision(dec);
          } catch {
            // No decision for this round
          }
        }
      } else {
        const simRes = await axios.get(`${API}/simulations/${simId}`);
        setSimulation(simRes.data);
        const hospRes = await axios.get(`${API}/simulations/${simId}/hospitals`);
        myHosp = hospRes.data.find(h => h.members?.includes(user.id));
        if (!myHosp) return;
        setHospital(myHosp);
        const [kpiRes, histRes, lbRes] = await Promise.all([
          axios.get(`${API}/hospitals/${myHosp.id}/kpis`),
          axios.get(`${API}/hospitals/${myHosp.id}/history`),
          axios.get(`${API}/simulations/${simId}/leaderboard`),
        ]);
        setKpis(kpiRes.data);
        setHistory(histRes.data);
        setLeaderboard(lbRes.data);
        if (simRes.data.current_round > 0) {
          try {
            const decRes = await axios.get(`${API}/hospitals/${myHosp.id}/decisions/${simRes.data.current_round}`);
            setDecision(decRes.data);
          } catch {
            // No decision for this round
          }
        }
      }
    } catch (err) {
      toast.error("Error loading hospital data");
    }
  }, [simId, routeHospitalId, user.id, isGodMode]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateDecision = (field, value) => setDecision(prev => ({ ...prev, [field]: value }));

  const saveDecision = async () => {
    if (!hospital || !decision) return;
    try {
      const { id, hospital_id, simulation_id, round_number, submitted, submitted_at, ...fields } = decision;
      await axios.put(`${API}/hospitals/${hospital.id}/decisions/${simulation.current_round}`, fields);
      toast.success("Saved");
      if (isGodMode && submitted) {
        setDecision(prev => ({ ...prev, submitted: false, submitted_at: null }));
      }
    } catch (e) {
      // Save error handled via toast
      toast.error(e.response?.data?.detail?.[0]?.msg || "Error saving");
    }
  };

  const submitDecision = async () => {
    await saveDecision();
    try {
      await axios.post(`${API}/hospitals/${hospital.id}/decisions/${simulation.current_round}/submit`);
      toast.success(t("hospital.decisionsSubmitted"));
      setWasSubmitted(true);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const goBack = () => {
    if (routeHospitalId) {
      navigate(user.role === "superadmin" ? "/admin" : "/instructor");
    } else {
      navigate("/simulations");
    }
  };

  if (!simulation || !hospital) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-400">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="hospital-dashboard">
      <DashboardHeader hospital={hospital} simulation={simulation} readOnly={false} goBack={goBack} logout={logout} />

      {isGodMode && (
        <div className="max-w-7xl mx-auto px-6 pt-4 space-y-2">
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2" data-testid="god-mode-banner">
            <Shield className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">God Mode</span>
            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">{user.role}</Badge>
            <span className="text-xs text-amber-600">— Editing {hospital.name}</span>
          </div>
          {wasSubmitted && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2" data-testid="decisions-submitted-banner">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">
                {hospital.name} has submitted their decisions for Year {simulation.current_round}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-5">
        {simulation.status === "active" && <MarketAlertBanner simId={simulation.id} />}

        <Tabs defaultValue="dashboard">
          <TabsList className="bg-white border border-slate-200 shadow-sm">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">{t("hospital.dashboard")}</TabsTrigger>
            <TabsTrigger value="results" data-testid="tab-results">{t("hospital.results")}</TabsTrigger>
            <TabsTrigger value="decisions" data-testid="tab-decisions">{t("hospital.decisions")}</TabsTrigger>
            <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">{t("hospital.leaderboard")}</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-5">
            <KPIGrid kpis={kpis} />
            {history.length > 0 && <PerformanceHistory hospitalId={hospital.id} history={history} hospital={hospital} />}
          </TabsContent>

          <TabsContent value="results" className="space-y-4">
            <RoundDetailPanel hospitalId={hospital.id} currentRound={simulation.current_round} />
          </TabsContent>

          <TabsContent value="decisions" className="space-y-4">
            <DecisionsPanel
              decision={decision}
              hospital={hospital}
              simulation={simulation}
              readOnly={false}
              updateDecision={updateDecision}
              saveDecision={saveDecision}
              submitDecision={submitDecision}
            />
          </TabsContent>

          <TabsContent value="leaderboard">
            <LeaderboardPanel leaderboard={leaderboard} hospitalId={hospital.id} />
          </TabsContent>
        </Tabs>
      </div>

      {simulation.status === "active" && <CopilotChat hospitalId={hospital.id} hospitalName={hospital.name} />}
      {hospital.members?.length > 0 && <TeamChat hospitalId={hospital.id} members={hospital.members} />}
    </div>
  );
}
